// Client-side video compression using canvas + MediaRecorder (no server ffmpeg).
// Re-encodes video to a downscaled stream targeting <= 50 MB. Falls back to raw
// upload when the browser cannot re-encode but the source is already small enough.
import emproLogo from "@/assets/empro-logo.png";

export const MAX_VIDEO_BYTES = 50 * 1024 * 1024;

let _wmImg = null;
const loadWatermark = () => new Promise((res) => {
  if (_wmImg && _wmImg.complete && _wmImg.naturalWidth) return res(_wmImg);
  const im = new Image();
  im.onload = () => { _wmImg = im; res(im); };
  im.onerror = () => res(null);
  im.src = emproLogo;
});

const pickMime = () => {
  const candidates = [
    "video/mp4;codecs=avc1.42E01E,mp4a.40.2",
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
  ];
  if (typeof MediaRecorder === "undefined") return null;
  return candidates.find((m) => MediaRecorder.isTypeSupported(m)) || null;
};

const rawResult = (file) => ({
  blob: file,
  mime: file.type && file.type.startsWith("video/") ? file.type : "video/mp4",
  raw: true,
});

export async function compressVideo(file, onProgress) {
  const mime = pickMime();
  if (!mime) {
    if (file.size <= MAX_VIDEO_BYTES) return rawResult(file);
    throw new Error("Przeglądarka nie obsługuje kompresji wideo. Użyj krótszego pliku (maks. 50 MB).");
  }

  const url = URL.createObjectURL(file);
  const video = document.createElement("video");
  video.muted = true;
  video.playsInline = true;
  video.preload = "auto";
  video.src = url;

  try {
    await new Promise((res, rej) => {
      video.onloadedmetadata = res;
      video.onerror = () => rej(new Error("Nie można odczytać pliku wideo"));
    });

    const vw = video.videoWidth;
    const vh = video.videoHeight;
    const duration = video.duration || 0;
    if (!vw || !vh) {
      if (file.size <= MAX_VIDEO_BYTES) return rawResult(file);
      throw new Error("Nie można zdekodować wideo w tej przeglądarce. Spróbuj w Chrome lub użyj pliku .mp4.");
    }

    const maxW = 1280;
    const scale = Math.min(1, maxW / vw);
    const w = Math.max(2, Math.round((vw * scale) / 2) * 2);
    const h = Math.max(2, Math.round((vh * scale) / 2) * 2);

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    const wm = await loadWatermark();

    let bitrate = 2500000;
    if (duration > 0) bitrate = Math.min(bitrate, Math.floor((MAX_VIDEO_BYTES * 8 * 0.85) / duration));
    bitrate = Math.max(400000, bitrate);

    const stream = canvas.captureStream(30);
    try {
      const vstream = video.captureStream ? video.captureStream() : (video.mozCaptureStream ? video.mozCaptureStream() : null);
      if (vstream) vstream.getAudioTracks().forEach((t) => stream.addTrack(t));
    } catch { /* audio optional */ }

    const recorder = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: bitrate });
    const chunks = [];
    recorder.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
    const stopped = new Promise((res) => { recorder.onstop = res; });

    recorder.start(1000);
    await video.play();

    let raf;
    const draw = () => {
      ctx.drawImage(video, 0, 0, w, h);
      if (wm && wm.naturalWidth) {
        const lw = w * 0.26;
        const lh = lw * (wm.naturalHeight / wm.naturalWidth);
        const m = Math.round(w * 0.025);
        ctx.save();
        ctx.globalAlpha = 0.55;
        ctx.drawImage(wm, w - lw - m, h - lh - m, lw, lh);
        ctx.restore();
      }
      if (duration) onProgress?.(Math.min(0.99, video.currentTime / duration));
      if (!video.ended) raf = requestAnimationFrame(draw);
    };
    draw();

    await new Promise((res) => { video.onended = res; });
    if (raf) cancelAnimationFrame(raf);
    recorder.stop();
    await stopped;

    const outMime = mime.split(";")[0];
    const blob = new Blob(chunks, { type: outMime });
    onProgress?.(1);
    if (blob.size > MAX_VIDEO_BYTES) {
      throw new Error("Skompresowany film nadal przekracza 50 MB — nagraj krótszy klip.");
    }
    return { blob, mime: outMime, raw: false };
  } finally {
    URL.revokeObjectURL(url);
  }
}
